import { useEffect, useState } from "react";
import { Printer } from "lucide-react";
import {
  Button,
  PrintRegion,
  Skeleton,
  StatusBadge,
  printRegion,
} from "@/design-system/components/ui";
import { ResourceDrawer } from "@/components/portfolioAdmin/loanDetail/common/ResourceDrawer";
import { useDateFormatter } from "@/utils/datetime";
import { InlineQueryError } from "../queryStates";
import { buyerLabel } from "../buyerFilters";
import { verdictTone } from "../buyerListBits";
import { InterviewCompare } from "./InterviewCompare";
import { InterviewPrintView } from "./InterviewPrintView";
import { InterviewRecord } from "./InterviewRecord";
import { defaultCompareVersion } from "./interviewCompare";
import {
  recordedWordingVersion,
  useBuyerInterviewVersion,
  useInterviewRegistry,
} from "./useBuyerInterview";

/**
 * One COMPLETED interview, read-only.
 *
 * <p>The point of the version history: a profile letter already sent can be
 * traced to the answers behind it, and the answers that drove the result are
 * marked where they sit. Nothing here writes — re-interviewing is the only way
 * to change an answer, and it mints a new version.</p>
 *
 * <p>🔴 The body is `InterviewRecord`, never `InterviewTranscript`. The
 * transcript is what the LIVE interview's review step renders, and it has no
 * place for a flag on purpose; the flags live only here.</p>
 *
 * <p>The version renders in the wording it was ASKED with: the registry is
 * read for the interview's own `wordingVersion` (0 — the coded defaults — when
 * it carries none), never the current wording, so a later rewording does not
 * rewrite what a past interview appears to have asked.</p>
 */
export function InterviewVersionDrawer({
  buyerLeadId,
  buyerName,
  version,
  onClose,
  completedVersions = [],
}: {
  buyerLeadId: string;
  /** Printed at the top of the print view. */
  buyerName?: string;
  /** Null closes the drawer — the caller holds the selection. */
  version: number | null;
  onClose: () => void;
  /** Every completed version this buyer has. Comparing is offered only when
   *  there is another one to compare with. */
  completedVersions?: number[];
}) {
  const { dateTime } = useDateFormatter();
  // The print view is mounted only while printing, so its rows never sit in
  // the DOM beside the drawer's own. The effect runs after the portal has
  // committed; `printRegion()` blocks until the dialog closes.
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    if (!printing) return;
    printRegion();
    setPrinting(false);
  }, [printing]);
  // Null = the single-interview view. The choice is stored WITH the version it
  // was made on, so a comparison never carries across to another version —
  // not even for the one render an effect-based reset would leave stale.
  const [compareChoice, setCompareChoice] = useState<{
    version: number;
    with: number;
  } | null>(null);
  const compareWith =
    compareChoice != null && compareChoice.version === version ? compareChoice.with : null;
  const setCompareWith = (other: number | null) =>
    setCompareChoice(other != null && version != null ? { version, with: other } : null);
  const otherVersions = [...completedVersions]
    .filter((v) => v !== version)
    .sort((a, b) => b - a);
  const versionQuery = useBuyerInterviewVersion(buyerLeadId, version);
  const interview = versionQuery.data;
  const completed = interview?.status === "Completed" ? interview : null;
  // Only a completed version has anything to render, so only it reads wording.
  const registryQuery = useInterviewRegistry(
    completed ? recordedWordingVersion(completed) : 0,
    version != null && completed != null,
  );

  const registry = registryQuery.data;
  const loading = registryQuery.isLoading || versionQuery.isLoading;
  const failed = registryQuery.isError || versionQuery.isError;
  const verdict = completed?.outcome?.effectiveVerdict ?? null;

  const subtitle = completed
    ? [
        completed.conductedByUserName || "Interviewer not recorded",
        completed.completedAtUtc ? dateTime(completed.completedAtUtc) : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : undefined;

  return (
    <ResourceDrawer
      open={version != null}
      onClose={onClose}
      title={`Interview v${version ?? ""}`}
      subtitle={subtitle}
      headerActions={
        verdict ? (
          <>
            <span data-testid="interview-version-result">
              <StatusBadge tone={verdictTone(verdict)}>{buyerLabel(verdict)}</StatusBadge>
            </span>
            {registry && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setPrinting(true)}
                data-testid="interview-print-button"
              >
                <Printer size={14} aria-hidden />
                Print
              </Button>
            )}
          </>
        ) : undefined
      }
      width="lg"
      testId="interview-version-drawer"
    >
      {loading && <Skeleton className="h-64 w-full" />}

      {!loading && failed && (
        <InlineQueryError
          what="this interview version"
          onRetry={() => {
            void versionQuery.refetch();
            void registryQuery.refetch();
          }}
          retrying={versionQuery.isFetching || registryQuery.isFetching}
        />
      )}

      {!loading && !failed && interview && !completed && (
        <p className="text-small text-gray-mid m-0" data-testid="interview-version-not-completed">
          This interview has not been completed, so there is nothing to review yet.
        </p>
      )}

      {!loading && !failed && completed && registry && otherVersions.length > 0 && (
        <div className="mb-space-4">
          {compareWith == null ? (
            <Button
              variant="text"
              size="sm"
              onClick={() =>
                setCompareWith(defaultCompareVersion(completed.version, otherVersions))
              }
              data-testid="interview-compare-open"
            >
              Compare with another version
            </Button>
          ) : (
            <Button
              variant="text"
              size="sm"
              onClick={() => setCompareWith(null)}
              data-testid="interview-compare-close"
            >
              Back to this interview
            </Button>
          )}
        </div>
      )}

      {!loading && !failed && completed && registry && compareWith == null && (
        <InterviewRecord interview={completed} registry={registry} />
      )}

      {!loading && !failed && completed && registry && compareWith != null && (
        <InterviewCompare
          key={`${completed.version}-${compareWith}`}
          buyerLeadId={buyerLeadId}
          current={completed}
          registry={registry}
          versions={otherVersions}
          compareWith={compareWith}
          onCompareWith={setCompareWith}
        />
      )}

      {printing && completed && registry && (
        <PrintRegion>
          <InterviewPrintView interview={completed} registry={registry} buyerName={buyerName} />
        </PrintRegion>
      )}
    </ResourceDrawer>
  );
}
